<script setup lang="ts">
import FinestGauge from '@/components/FinestGauge.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  fields: { label: string; filled: boolean }[]
}>()

const { t } = useI18n()

const filled = computed(() => props.fields.filter((field) => field.filled))

const completion = computed(() =>
  props.fields.length ? Math.round((filled.value.length / props.fields.length) * 100) : 0,
)

const openFields = computed(() => props.fields.filter((field) => !field.filled))
</script>

<template>
  <div class="flex flex-col gap-6">
    <p class="section-label">{{ t('eyebrow') }}</p>

    <div class="text-center">
      <FinestGauge :value="completion" :caption="t('gaugeCaption')" :label="t('gaugeLabel', { percent: completion })" />
      <p class="text-ink-2 mt-1.5 text-sm leading-relaxed">{{ t('intro') }}</p>
      <p class="text-ink-3 mt-2 text-xs">
        {{ t('progress', { filled: filled.length, total: fields.length }) }}
      </p>
    </div>

    <div class="border-line border-t pt-4">
      <p class="text-ink mb-3 font-medium">
        {{ openFields.length ? t('open.title') : t('complete.title') }}
      </p>

      <ul v-if="openFields.length" class="flex flex-col gap-2">
        <li v-for="field in openFields" :key="field.label" class="text-ink-2 flex items-center gap-2 text-sm">
          <span class="border-line size-4 flex-none rounded-full border border-dashed" aria-hidden="true" />
          {{ field.label }}
        </li>
      </ul>

      <Message v-else severity="success" icon="pi pi-check-circle" size="small">
        <span class="text-sm">{{ t('complete.description') }}</span>
      </Message>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "eyebrow": "Assistent",
    "gaugeCaption": "erfasst",
    "gaugeLabel": "{percent} % der Grunddaten erfasst",
    "intro": "So weit sind die Grunddaten dieser Immobilie erfasst. Je vollständiger die Angaben, desto schneller ist das Objekt bankreif.",
    "progress": "{filled} von {total} Angaben ausgefüllt",
    "open": {
      "title": "Noch offen"
    },
    "complete": {
      "title": "Grunddaten vollständig",
      "description": "Alle Angaben dieses Assistenten sind erfasst. Weitere Details ergänzt du in der Detailansicht."
    }
  },
  "en": {
    "eyebrow": "Assistant",
    "gaugeCaption": "captured",
    "gaugeLabel": "{percent} % of the basic data captured",
    "intro": "This is how much of the property's basic data is captured. The more complete the details, the sooner the property is bank-ready.",
    "progress": "{filled} of {total} details filled in",
    "open": {
      "title": "Still open"
    },
    "complete": {
      "title": "Basic data complete",
      "description": "Every detail of this assistant is captured. You add further details in the detail view."
    }
  }
}
</i18n>
